<!--
  ClientDashboard.vue — replaces EmbedPowerBIDashboard.vue (client-dashboard replacement, 2026-06-10).
  NEW FILE → drop into web/src/views/apps/campaigns/client-dashboard/ClientDashboard.vue
  NOTE: not compile-verified (no Vue build available to Claude) — Manish to test locally.

  Renders the campaign's permitted internal dashboards as sandboxed iframes. For each
  dashboard it fetches the gated data (/client-dashboard-data/<id>, server-enforces D1)
  and injects it as window.__DASHBOARD_DATA__ into the bespoke HTML (srcdoc). The
  bespoke HTML reads window.__DASHBOARD_DATA__ (it does NOT call Supabase directly).
  If the user has no permitted dashboard for the campaign, the holding message shows.
-->
<template>
  <div class="vx-row">
    <vx-card v-if="dashboards.length != 0">
      <template v-for="(dashboard, index) in dashboards">
        <div class="text-center" :key="'h' + index">
          <h5>{{ dashboard.name }}</h5>
          <p v-if="dashboard.updatedAt && !dashboard.unavailable" class="text-sm mt-1">
            Last updated {{ formatUtc(dashboard.updatedAt) }} · all times UTC
            <vs-button
              v-if="dashboard.refreshable"
              size="small"
              type="border"
              icon="refresh"
              class="ml-3"
              :disabled="dashboard.refreshing"
              @click="refresh(dashboard)"
            >{{ dashboard.refreshing ? "Refreshing…" : "Refresh" }}</vs-button>
          </p>
          <p v-if="dashboard.refreshNote" class="text-sm mt-1">{{ dashboard.refreshNote }}</p>
        </div>
        <div :key="'f' + index">
          <div v-if="dashboard.unavailable" class="text-center p-10">
            This dashboard is temporarily unavailable. Please try again shortly.
          </div>
          <iframe
            v-else
            :srcdoc="dashboard.srcdoc"
            class="client_dashboard_frame"
            sandbox="allow-scripts allow-popups"
            frameborder="0"
          ></iframe>
        </div>
        <vs-divider class="mb-10 mt-10" :key="'d' + index" />
      </template>
    </vx-card>

    <vx-card v-else-if="loaded">
      <div class="text-center holding_message">
        <p>We are currently updating our dashboards to deliver even more insight and refresh more often. If you need an updated dashboard right now, please contact your campaign manager.</p>
        <p>Kind regards</p>
        <p>The Elomark Team</p>
      </div>
    </vx-card>
  </div>
</template>

<script>
import axios from "@/axios.js";
import defineAbilitiesFor from "@/config/abilities";

// Refresh (emp-dash only): a refresh normally lands in under 35 s; it can also
// legitimately never land (emp-dash keeps the previous figures), so the poll
// gives up after POLL_GIVE_UP_MS and says so.
const POLL_INTERVAL_MS = 5000;
const POLL_GIVE_UP_MS = 60000;
import moduleCampaignManagement from "@/store/campaign-management/moduleCampaignManagement.js";

export default {
  data: () => ({
    authorized: true,
    loaded: false,
    user_id: null,
    user_role: null,
    campaign_id: null,
    campaignDescription: null,
    dashboards: [],
    abilities: null,
    destroyed: false
  }),
  methods: {
    checkClientUserCampaignPermission() {
      let data = {};
      data.campaign_id = this.$route.params.campaignId;
      data.user_id = this.user_id;
      this.$store
        .dispatch("campaignsManagement/checkClientUserCampaignPermission", data)
        .then(res => {
          let result = JSON.parse(res.data);
          if (result.length == 0) {
            this.authorized = false;
            this.$router.replace("/unauthorized");
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 401) {
            this.$router.replace("/login");
          }
        });
    },

    getCampaignDescription() {
      let self = this;
      self.$vs.loading();
      const campaignId = this.$route.params.campaignId;
      self.$store
        .dispatch("campaignsManagement/fetchCampaign", campaignId)
        .then(res => {
          self.campaignDescription = res.data;
          let permitted = [];
          let all = (self.campaignDescription && self.campaignDescription.campaign_dashboard) || [];
          all.forEach(function (d) {
            if (d.permission) {
              d.permission.forEach(function (p) {
                if (p.uuid == self.user_id) {
                  permitted.push(d);
                }
              });
            }
          });
          // de-dupe by dashboard id (uuid)
          const unique = [...new Map(permitted.map(item => [item["uuid"], item])).values()];
          self.loadDashboards(unique);
        })
        .catch(err => {
          self.$vs.loading.close();
          if (err.response && err.response.status === 401) {
            this.$router.replace("/login");
          }
        });
    },

    loadDashboards(list) {
      let self = this;
      if (!list.length) {
        self.loaded = true;
        self.$vs.loading.close();
        return;
      }
      let pending = list.length;
      const done = () => {
        pending -= 1;
        if (pending === 0) {
          self.loaded = true;
          self.$vs.loading.close();
        }
      };
      list.forEach(function (src) {
        // A fresh copy with every field present before it joins the list, so Vue
        // tracks them all. (The originals are already reactive - they come from
        // campaignDescription - and fields added to them later would not update the page.)
        const d = Object.assign({}, src, {
          srcdoc: "",
          updatedAt: null,
          refreshable: false,
          refreshing: false,
          refreshNote: "",
          unavailable: false
        });

        if (!d.data_set_id) {
          // A fixed page with no data feed: shown exactly as before.
          d.srcdoc = self.buildSrcdoc(d.html, null);
          self.dashboards.push(d);
          done();
          return;
        }
        self.$store
          .dispatch("campaignsManagement/getClientDashboardData", d.id)
          .then(res => {
            self.applyData(d, res.data);
            self.dashboards.push(d);
          })
          .catch(() => {
            // Same message whatever went wrong; the server log records which failure it was.
            d.unavailable = true;
            self.dashboards.push(d);
          })
          .then(done);
      });
    },

    applyData(d, data) {
      const x = data || {};
      d.updatedAt = x.updated_at || null;
      d.refreshable = x.source === "emp-dash" && x.refreshable === true;
      // The dashboard HTML gets exactly what it got before: { payload, updated_at }.
      d.srcdoc = this.buildSrcdoc(d.html, { payload: x.payload, updated_at: x.updated_at });
    },

    refresh(d) {
      if (d.refreshing) return;
      const self = this;
      const before = d.updatedAt;
      d.refreshing = true;
      d.refreshNote = "Refreshing… this usually takes under a minute.";
      axios.post("/api/campaigns/client-dashboard-refresh/" + d.id)
        .then(() => self.poll(d, before, Date.now()))
        .catch(err => {
          const status = err && err.response && err.response.status;
          if (status === 409) {
            d.refreshable = false;
            d.refreshNote = "This dashboard is final and is not refreshed.";
          } else {
            d.refreshNote = "Refresh is temporarily unavailable. The figures shown are unchanged.";
          }
          d.refreshing = false;
        });
    },

    poll(d, before, started) {
      const self = this;
      if (self.destroyed) return;
      if (Date.now() - started > POLL_GIVE_UP_MS) {
        d.refreshing = false;
        d.refreshNote = "No newer figures yet. Still showing the figures from " + self.formatUtc(before) + ".";
        return;
      }
      setTimeout(() => {
        if (self.destroyed) return;
        self.$store
          .dispatch("campaignsManagement/getClientDashboardData", d.id)
          .then(res => {
            if (res.data && res.data.updated_at && res.data.updated_at !== before) {
              self.applyData(d, res.data);
              d.refreshing = false;
              d.refreshNote = "Updated.";
            } else {
              self.poll(d, before, started);
            }
          })
          // a failed poll leaves the current figures on screen and keeps waiting
          .catch(() => self.poll(d, before, started));
      }, POLL_INTERVAL_MS);
    },

    formatUtc(iso) {
      if (!iso) return "";
      const t = new Date(iso);
      if (isNaN(t.getTime())) return iso;
      return t.toISOString().slice(0, 16).replace("T", " ") + " UTC";
    },

    buildSrcdoc(html, data) {
      // Avoid literal script tags in source so the SFC parser is not confused.
      var open = "<" + "script>";
      var close = "<" + "/script>";
      var inject = open + "window.__DASHBOARD_DATA__ = " + JSON.stringify(data || {}) + ";" + close;
      return inject + (html || "");
    }
  },
  mounted() {
    if (this.authorized) {
      this.getCampaignDescription();
    }
  },
  beforeDestroy() {
    this.destroyed = true;
  },
  created() {
    var self = this;
    defineAbilitiesFor().then(function (data) {
      self.abilities = data;
    });
    let userInfo = JSON.parse(localStorage.getItem("userInfo"));
    self.user_id = userInfo["uid"];
    self.user_role = userInfo["userRole"];
    self.campaign_id = this.$route.params.campaignId;
    if (this.user_role == "Sales Developer") {
      this.authorized = false;
      this.$router.replace("/unauthorized");
    }
    if (!moduleCampaignManagement.isRegistered) {
      this.$store.registerModule("campaignsManagement", moduleCampaignManagement);
      moduleCampaignManagement.isRegistered = true;
    }
    this.checkClientUserCampaignPermission();
  }
};
</script>

<style>
.client_dashboard_frame {
  width: 100%;
  height: 80vh;
  border: none;
}
.holding_message {
  padding: 40px;
}
</style>